import {
  DELETION_FLAG,
  ERROR_CODE,
  type FallKey,
  FallRegistrationFormInput,
  FallRegistrationFormSchema,
  TIME_FRAME,
  UniversalError,
  YearMonthSchema,
  isDisabled,
  parseData,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import { Fragment, useId, useMemo, useState } from "react";
import { LuChevronLeft, LuChevronRight, LuPlus, LuTrash } from "react-icons/lu";
import { Temporal } from "temporal-polyfill";
import { type Merge } from "type-fest";
import * as v from "valibot";
import {
  ButtonStyle,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  FormFieldProperties,
  PaperStyle,
  ProgressOverlay,
  TypographyStyle,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  registerFall,
  removeFallCache,
  removeFallsCache,
  unregisterFall,
  useFall,
  useGetErrorMessage,
  useListedFalls,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/falls/registration",
)({
  component: Component,
  validateSearch: v.partial(
    v.object({
      yearMonth: v.fallback(YearMonthSchema(), function () {
        return Temporal.Now.plainDateISO("Asia/Tokyo").toPlainYearMonth();
      }),
    }),
  ),
});

function Component() {
  const { facilityKey, userKey } = Route.useParams();
  const { yearMonth: _yearMonth } = Route.useSearch();

  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const router = useRouter();
  const translate = useTranslate();
  const [progress, setProgress] = useState(100);

  const yearMonth = useMemo(
    function () {
      return _yearMonth ?? Temporal.Now.plainDateISO("Asia/Tokyo").toPlainYearMonth();
    },
    [_yearMonth],
  );

  const month = useMemo(
    function () {
      return [
        yearMonth.toPlainDate({ day: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
        yearMonth
          .add({ months: 1 })
          .toPlainDate({ day: 1 })
          .toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
      ] as const;
    },
    [yearMonth],
  );

  const { pages } = useListedFalls(
    { deleted: DELETION_FLAG.DEFAULT, fellAt: ["-", ...month], userKey },
    { descendingOrder: true },
  );

  const falls = useMemo(
    function () {
      return pages.flatMap(function (page) {
        return page.falls;
      });
    },
    [pages],
  );

  const formId = useId();

  const schema = FallRegistrationFormSchema();
  const defaultValues = useMemo(function (): Partial<FallRegistrationFormInput> {
    return { falls: [] };
  }, []);

  const { Field, Subscribe, handleSubmit, setFieldValue, store } = useForm({
    defaultValues,
    async onSubmit({ formApi, value: input }) {
      setProgress(0);

      try {
        const output = parseData(schema, input as any);
        console.info("[onSubmit]", new Date(), { input, output });

        let completelySucceeded = true;

        for (let i = 0; i < output.falls.length; i++) {
          const { fellAt } = output.falls[i];
          try {
            const { fall } = await registerFall({ fellAt, userKey });
            console.debug("転倒データを登録しました。", { fall });
            formApi.setFieldValue("falls", function (value) {
              return value?.filter(function (_, j) {
                return i !== j;
              });
            });
          } catch (error) {
            showError(error);
            completelySucceeded = false;
          } finally {
            setProgress(((i + 1) / output.falls.length) * 100);
          }
        }

        removeFallsCache();
        setProgress(100);

        console.info("全ての転倒データを処理しました。");

        await $alert({
          color: "success",
          content: translate("%Register.1%"),
          title: translate("%Register.0%"),
        });

        if (completelySucceeded) {
          if (router.history.canGoBack()) {
            router.history.back();
          } else {
            await navigate({
              ignoreBlocker: true,
              params: { facilityKey, userKey },
              to: "/facilities/$facilityKey/residents/$userKey",
            });
          }
        }
      } catch (error) {
        showError(error);
      }
    },
    async onSubmitInvalid({ value: input }) {
      console.info("[onSubmitInvalid]", new Date(), { input });
      showError(new UniversalError(ERROR_CODE.INVALID_FORM));
    },
    validators: { onChange: schema },
  });

  {
    const isDefaultValue = useStore(store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(store, (state) => state.isSubmitted);
    const isSubmitting = useStore(store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <main>
      <ProgressOverlay progress={progress} />

      <div className={css({ display: "flex", gap: "8px", justifyContent: "flex-end" })}>
        <Link
          className={ButtonStyle({
            appearance: "ghost",
            borderRadius: "4px",
            color: "secondary",
            fontSize: "14px",
            padding: "10px 16px",
          })}
          onClick={function (event) {
            if (router.history.canGoBack()) {
              event.preventDefault();
              router.history.back();
            }
          }}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          {translate("%Cancel.0%")}
        </Link>
        <Subscribe
          selector={function ({ canSubmit }) {
            return canSubmit;
          }}
        >
          {function (canSubmit) {
            return (
              <button
                className={ButtonStyle({
                  appearance: "contained",
                  borderRadius: "4px",
                  color: "primary",
                  fontSize: "14px",
                  padding: "10px 16px",
                })}
                disabled={!canSubmit}
                form={formId}
                type="submit"
              >
                {translate("%Save.0%")}
              </button>
            );
          }}
        </Subscribe>
      </div>

      <form
        className={PaperStyle({ borderRadius: "12px", marginTop: "12px", padding: "16px" })}
        id={formId}
        onSubmit={function (event) {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div
          className={css({ display: "flex", justifyContent: "space-between", marginTop: "12px" })}
        >
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              icon: true,
              padding: "12px",
            })}
            params={{ facilityKey, userKey }}
            replace
            search={{ yearMonth: yearMonth.subtract({ months: 1 }) }}
            to="/facilities/$facilityKey/residents/$userKey/falls/registration"
          >
            <LuChevronLeft className={css({ height: "16px", width: "auto" })} />
          </Link>
          <p
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%YearMonth.1%", { $: yearMonth })}
          </p>
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              icon: true,
              padding: "12px",
            })}
            params={{ facilityKey, userKey }}
            replace
            search={{ yearMonth: yearMonth.add({ months: 1 }) }}
            to="/facilities/$facilityKey/residents/$userKey/falls/registration"
          >
            <LuChevronRight className={css({ height: "16px", width: "auto" })} />
          </Link>
        </div>

        <Field mode="array" name="falls">
          {function (field) {
            return (
              <Fragment>
                <div
                  className={css({
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "end",
                  })}
                >
                  <div
                    className={ButtonStyle({
                      appearance: "ghost",
                      borderRadius: "4px",
                      color: "ghost",
                      fontSize: "14px",
                      gap: "8px",
                      padding: "10px 16px",
                    })}
                    onClick={function () {
                      field.pushValue({
                        fellAt: Temporal.Now.plainDateTimeISO("Asia/Tokyo").with({
                          second: 0,
                          millisecond: 0,
                          microsecond: 0,
                          nanosecond: 0,
                        }),
                      });
                    }}
                  >
                    <LuPlus />
                    <p>{translate("%Add.1%")}</p>
                  </div>
                </div>

                {field.state.value?.map(function (_, index) {
                  return (
                    <Field key={index} name={`falls[${index}].fellAt`}>
                      {function (subfield) {
                        return (
                          <FormField
                            caution={getErrorMessage(subfield.state.meta.errors.at(0))}
                            className={css({ marginTop: "12px" })}
                            invalid={subfield.state.meta.errors.length > 0}
                            label={translate("%DateTime.0%")}
                          >
                            <div
                              className={css({
                                alignItems: "center",
                                display: "flex",
                                gap: "8px",
                                width: "100%",
                              })}
                            >
                              <FormFieldInput
                                className={css(
                                  TypographyStyle.raw({
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    monospace: true,
                                  }),
                                  FormFieldInputStyle.raw({ flexGrow: 1 }),
                                )}
                                onBlur={function () {
                                  subfield.handleBlur();
                                }}
                                onChange={function (event) {
                                  console.info("[onChange]", event);
                                  subfield.handleChange(
                                    Temporal.PlainDateTime.from(event.target.value),
                                  );
                                }}
                                type="datetime-local"
                                value={
                                  subfield.state.value instanceof Temporal.PlainDateTime
                                    ? subfield.state.value.toString()
                                    : ""
                                }
                              />
                              <button
                                className={ButtonStyle({
                                  color: "ghost",
                                  icon: true,
                                  size: "40px",
                                })}
                                onClick={function (event) {
                                  event.stopPropagation();
                                  event.preventDefault();
                                  setFieldValue("falls", function (value) {
                                    return value?.filter(function (_, i) {
                                      return i !== index;
                                    });
                                  });
                                }}
                              >
                                <LuTrash className={css({ height: "16px", width: "auto" })} />
                              </button>
                            </div>
                          </FormField>
                        );
                      }}
                    </Field>
                  );
                })}
              </Fragment>
            );
          }}
        </Field>

        {falls.map(function (fall) {
          return (
            <FallDisplay
              className={css({ marginTop: "12px" })}
              fallKey={fall.fallKey}
              key={fall.fallKey}
              label={translate("%DateTime.0%")}
            />
          );
        })}
      </form>

      <div className={css({ height: "16px" })} />
    </main>
  );
}

type FallDisplayProperties = Merge<FormFieldProperties, { fallKey: FallKey }>;
function FallDisplay({ className, fallKey, ...properties }: FallDisplayProperties) {
  const fall = useFall(fallKey);
  const translate = useTranslate();

  const [loading, setLoading] = useState(false);

  return (
    <FormField
      {...properties}
      caution={translate("%Deleted.0%")}
      className={cx(className)}
      invalid={isDisabled(fall)}
    >
      {fall ? (
        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "8px",
            width: "100%",
          })}
        >
          <p
            className={TypographyStyle({
              color: "primary",
              ellipsis: true,
              fontSize: "14px",
              lineHeight: 1,
              monospace: true,
              width: "stretch",
            })}
          >
            {translate("%DateTime.3%", { $: fall.fellAt })}
          </p>
          <button
            className={ButtonStyle({ color: "ghost", icon: true, size: "40px" })}
            disabled={loading}
            onClick={async function (event) {
              event.stopPropagation();
              event.preventDefault();
              try {
                setLoading(true);
                await unregisterFall({ fallKey });
                removeFallCache(fallKey);
              } catch (error) {
                showError(error);
              } finally {
                setLoading(false);
              }
            }}
          >
            <LuTrash className={css({ height: "16px", width: "auto" })} />
          </button>
        </div>
      ) : (
        <p>{translate("%NoData.0%")}</p>
      )}
    </FormField>
  );
}
